<script setup>
import { tv } from 'tailwind-variants'

/**
 * @typedef {object} DateItemProps
 * @property {'default' | 'disabled' | 'active' | 'empty' | 'range-start' | 'range-end' | 'range-between' | 'range-single'} [variant='default'] - The visual variant of the date item
 * @property {boolean} [disabled=false] - Whether the date item is disabled
 * @property {boolean} [showContent=true] - Whether to show the content or render an empty placeholder
 */

/**
 * @type {DateItemProps}
 */
const props = defineProps({
  variant: {
    type: String,
    default: 'default',
    validator: value => ['default', 'disabled', 'active', 'empty', 'range-start', 'range-end', 'range-between', 'range-single'].includes(value),
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  showContent: {
    type: Boolean,
    default: true,
  },
})

const emit = defineEmits(['click', 'mouseenter', 'mouseleave'])

const dateItemThemes = tv({
  base: [
    'h-8 w-8',
    'text-xs font-medium',
    'rounded-lg flex items-center justify-center',
  ],
  variants: {
    variant: {
      'default': [
        'text-brand-gray-14',
        'hover:bg-brand-gray-15 hover:text-brand-gray-1',
        'cursor-pointer',
      ],
      'disabled': [
        'cursor-not-allowed',
        'text-brand-gray-12',
        'pointer-events-none',
      ],
      'active': [
        'bg-brand-surface-inverse text-brand-gray-1',
        'cursor-pointer',
      ],
      'range-start': [
        'bg-brand-surface-inverse text-brand-gray-1',
        'cursor-pointer',
        'rounded-l-lg rounded-r-none',
      ],
      'range-end': [
        'bg-brand-surface-inverse text-brand-gray-1',
        'cursor-pointer',
        'rounded-r-lg rounded-l-none',
      ],
      'range-between': [
        'bg-brand-gray-5 text-brand-primary-9',
        'cursor-pointer',
        'rounded-none',
      ],
      'range-single': [
        'bg-brand-surface-inverse text-brand-gray-1',
        'cursor-pointer',
        'rounded-lg',
      ],
      'empty': [
        'pointer-events-none',
        'cursor-default',
      ],
    },
  },
  defaultVariants: {
    variant: 'default',
  },
})

function handleClick() {
  if (!props.disabled && props.variant !== 'disabled' && props.showContent) {
    emit('click')
  }
}
</script>

<template>
  <button
    :class="dateItemThemes({ variant: props.showContent ? props.variant : 'empty' })"
    @click="handleClick"
    @mouseenter="$emit('mouseenter')"
    @mouseleave="$emit('mouseleave')"
  >
    <slot v-if="props.showContent" />
  </button>
</template>
